<template>
  <div
    v-if="totalResults > 0"
    class="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6 md:mt-8 space-y-3 sm:space-y-0 px-1 sm:px-2"
  >
    <!-- Results summary -->
    <div
      class="text-xs sm:text-sm text-gray-600 font-medium text-center sm:text-left"
    >
      <template v-if="!isLoading">
        Showing
        <span class="font-bold text-gray-900">
          {{ (currentPage - 1) * limit + 1 }}
        </span>
        to
        <span class="font-bold text-gray-900">
          {{ Math.min(currentPage * limit, totalResults) }}
        </span>
        of
        <span class="font-bold text-gray-900">
          {{ totalResults }}
        </span>
        results
      </template>
      <template v-else>
        <div class="flex items-center gap-2 justify-center sm:justify-start">
          <div
            class="w-3 h-3 sm:w-4 sm:h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
          ></div>
          <span>Loading results...</span>
        </div>
      </template>
    </div>

    <!-- Pagination buttons -->
    <div class="flex items-center justify-center gap-1">
      <!-- Previous button -->
      <button
        @click="$emit('go-to-page', currentPage - 1)"
        :disabled="currentPage === 1 || isLoading"
        class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] sm:min-h-[32px] min-w-[36px] sm:min-w-[32px] border border-gray-300 touch-manipulation"
      >
        <ChevronLeft class="w-3 h-3 sm:w-4 sm:h-4" />
      </button>

      <!-- Numbered buttons -->
      <template
        v-for="page in Math.min(totalPages, 5)"
        :key="page"
      >
        <button
          @click="$emit('go-to-page', page)"
          :disabled="isLoading"
          :class="[
            'px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-lg transition-all duration-200 min-h-[36px] sm:min-h-[32px] min-w-[36px] sm:min-w-[32px] border touch-manipulation',
            page === currentPage
              ? 'font-bold bg-blue-600 text-white border-blue-600'
              : 'font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-gray-300',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          ]"
        >
          {{ page }}
        </button>
      </template>

      <!-- Next button -->
      <button
        @click="$emit('go-to-page', currentPage + 1)"
        :disabled="currentPage === totalPages || isLoading"
        class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] sm:min-h-[32px] min-w-[36px] sm:min-w-[32px] border border-gray-300 touch-manipulation"
      >
        <ChevronRight class="w-3 h-3 sm:w-4 sm:h-4" />
      </button>
    </div>
  </div>
</template>

<script setup>
  import { ChevronLeft, ChevronRight } from "lucide-vue-next";

  defineProps({
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    totalResults: {
      type: Number,
      default: 0,
    },
    limit: {
      type: Number,
      default: 20,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["go-to-page"]);
</script>

<style scoped>
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
